import { useState, useCallback, useEffect, useRef } from 'react';
import type { PaginatedResponse, PaginationMeta, QueryParams } from '@yawi/domain';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import { useToast, getErrorMessage } from '@/app/providers';

interface UsePaginatedListOptions<T, F = Record<string, unknown>> {
  fetchFn: (params: QueryParams) => Promise<PaginatedResponse<T>>;
  initialFilters?: F;
  initialSearch?: string;
  pageSize?: number;
  autoFetch?: boolean;
  errorTitle?: string;
}

interface UsePaginatedListReturn<T, F> {
  // Data
  data: T[];
  meta: PaginationMeta | null;
  loading: boolean;
  error: string | null;

  // Pagination
  page: number;
  setPage: (page: number) => void;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  pageSize: number;
  setPageSize: (size: number) => void;

  // Search (debounced - auto-searches after 400ms)
  search: string;
  setSearch: (term: string) => void;

  // Filters
  filters: F;
  setFilters: (filters: Partial<F>) => void;
  clearFilters: () => void;

  // Actions
  refresh: () => void;
}

/**
 * Hook enterprise para listados paginados con:
 * - Paginación automática
 * - Búsqueda con debounce (400ms)
 * - Filtros dinámicos
 * - Manejo de errores con toast
 *
 * @example
 * const {
 *   data: units,
 *   meta,
 *   loading,
 *   page,
 *   setPage,
 *   search,
 *   setSearch,
 *   refresh
 * } = usePaginatedList({
 *   fetchFn: (params) => unitRepo.getUnits(params),
 *   initialFilters: { type: 'ALL' }
 * });
 */
export function usePaginatedList<T, F = Record<string, unknown>>(
  options: UsePaginatedListOptions<T, F>
): UsePaginatedListReturn<T, F> {
  const {
    fetchFn,
    initialFilters = {} as F,
    initialSearch = '',
    pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
    autoFetch = true,
    errorTitle = 'Error al cargar datos',
  } = options;

  const toast = useToast();
  const fetchFnRef = useRef(fetchFn);

  // State
  const [data, setData] = useState<T[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPageState] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [search, setSearch] = useState(initialSearch);
  const [pendingSearch, setPendingSearch] = useState(initialSearch);
  const [filters, setFiltersState] = useState<F>(initialFilters);

  // Ref to prevent double fetch on mount in StrictMode
  const fetchedRef = useRef(false);
  const requestIdRef = useRef(0);
  const hasLoadedDataRef = useRef(false);

  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  // Fetch data
  const fetchData = useCallback(
    async (pageNum: number, searchTerm: string, currentFilters: F, currentPageSize: number) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);
      setError(null);

      try {
        const params: QueryParams = {
          page: pageNum,
          limit: currentPageSize,
          search: searchTerm || undefined,
          ...currentFilters,
        };

        const response = await fetchFnRef.current(params);

        if (requestId !== requestIdRef.current) return;
        setData(response.data);
        setMeta(response.meta);
        hasLoadedDataRef.current = true;
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
        toast.error(errorMsg, errorTitle);
        setData([]);
        setMeta(null);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [toast, errorTitle]
  );

  // Auto fetch on mount
  useEffect(() => {
    if (autoFetch && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchData(page, search, filters, pageSize);
    }
  }, [autoFetch, fetchData, page, search, filters, pageSize]);

  // Pagination
  const setPage = useCallback(
    (newPage: number) => {
      if (newPage >= 1 && (!meta || newPage <= meta.totalPages)) {
        setPageState(newPage);
        fetchData(newPage, search, filters, pageSize);
      }
    },
    [meta, search, filters, pageSize, fetchData]
  );

  const goToNextPage = useCallback(() => {
    if (meta?.hasNextPage) {
      setPage(page + 1);
    }
  }, [meta, page, setPage]);

  const goToPrevPage = useCallback(() => {
    if (meta?.hasPreviousPage) {
      setPage(page - 1);
    }
  }, [meta, page, setPage]);

  // Page size change - reset to page 1
  const setPageSize = useCallback(
    (newSize: number) => {
      setPageSizeState(newSize);
      setPageState(DEFAULT_PAGE);
      fetchData(DEFAULT_PAGE, search, filters, newSize);
    },
    [search, filters, fetchData]
  );

  // Search with debounce - auto-search after 400ms of no typing
  const handleSetSearch = useCallback((term: string) => {
    setPendingSearch(term);
  }, []);

  // Debounce effect for search
  useEffect(() => {
    // Skip on initial mount (handled by autoFetch)
    if (!fetchedRef.current) return;
    if (pendingSearch === search) return;

    const timer = setTimeout(() => {
      setSearch(pendingSearch);
      setPageState(DEFAULT_PAGE);
      fetchData(DEFAULT_PAGE, pendingSearch, filters, pageSize);
    }, 400);

    return () => clearTimeout(timer);
  }, [pendingSearch, search, filters, pageSize, fetchData]);

  // Filters
  const setFilters = useCallback(
    (newFilters: Partial<F>) => {
      const updated = { ...filters, ...newFilters } as F;
      setFiltersState(updated);
      setPageState(DEFAULT_PAGE);
      fetchData(DEFAULT_PAGE, search, updated, pageSize);
    },
    [filters, search, pageSize, fetchData]
  );

  const clearFilters = useCallback(() => {
    setFiltersState(initialFilters);
    setPendingSearch('');
    setSearch('');
    setPageState(DEFAULT_PAGE);
    fetchData(DEFAULT_PAGE, '', initialFilters, pageSize);
  }, [initialFilters, pageSize, fetchData]);

  // Refresh
  const refresh = useCallback(() => {
    fetchData(page, search, filters, pageSize);
  }, [page, search, filters, pageSize, fetchData]);

  return {
    // Data
    data,
    meta,
    loading,
    error,

    // Pagination
    page,
    setPage,
    goToNextPage,
    goToPrevPage,
    pageSize,
    setPageSize,

    // Search (debounced)
    search: pendingSearch,
    setSearch: handleSetSearch,

    // Filters
    filters,
    setFilters,
    clearFilters,

    // Actions
    refresh,
  };
}
